import { Box, useBreakpoints } from "@sembark-travel/ui-base"
import { Helmet } from "react-helmet-async"
import { Outlet, useParams } from "react-router"
import { generatePath } from "../../../../../router-utils"
import { WhatsAppInboxThreadsList } from "../../../../../WhatsAppMessages"
import { useTripDetailsOutletContext } from "../_trip-details-context"

/**
 * The trip's own WhatsApp view: the same two-pane conversation UI as the inbox,
 * narrowed to the threads linked to this trip.
 */
export default function TripWhatsAppLayoutPage() {
	const { trip, onChange } = useTripDetailsOutletContext()
	const params = useParams()
	const activeThreadId = params.threadId ? Number(params.threadId) : undefined
	const tripId = Number(trip.id)

	// Rows stay inside the trip so the operator keeps the trip's tabs and
	// context, rather than being thrown out to the global inbox.
	const threadPath = (threadId: number) =>
		generatePath("/trips/:tripId/whatsapp/:threadId", {
			tripId: String(tripId),
			threadId: String(threadId),
		})

	// Three panes only fit on wide screens; below that show one at a time.
	const { lg, xl } = useBreakpoints()
	const isDesktop = lg || xl

	// Bounded rather than the inbox's full-viewport shell — this sits inside the
	// trip's tab content.
	const shellStyle = { height: "70vh", minHeight: 0 } as const

	if (!isDesktop) {
		return (
			<>
				<Helmet>
					<title>WhatsApp | Trip</title>
				</Helmet>
				<Box
					borderWidth="1"
					rounded="md"
					style={{ ...shellStyle, overflow: "hidden" }}
				>
					{activeThreadId ? (
						<Outlet context={{ trip, onChange }} />
					) : (
						<WhatsAppInboxThreadsList
							activeThreadId={activeThreadId}
							modelType="trips"
							modelId={tripId}
							threadPath={threadPath}
						/>
					)}
				</Box>
			</>
		)
	}

	return (
		<>
			<Helmet>
				<title>WhatsApp | Trip</title>
			</Helmet>
			<Box
				display="grid"
				borderWidth="1"
				rounded="md"
				style={{
					...shellStyle,
					gridTemplateColumns: "320px minmax(0, 1fr)",
				}}
			>
				<Box
					borderRightWidth="1"
					bgColor="default"
					style={{ minHeight: 0, overflow: "hidden" }}
				>
					<WhatsAppInboxThreadsList
						activeThreadId={activeThreadId}
						modelType="trips"
						modelId={tripId}
						threadPath={threadPath}
					/>
				</Box>
				<Box style={{ minHeight: 0, overflow: "hidden" }}>
					<Outlet context={{ trip, onChange }} />
				</Box>
			</Box>
		</>
	)
}
